import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, FileText, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../contexts/AuthContext';

const categories = ['All', 'Operations', 'Safety', 'Quality', 'Maintenance', 'Training', 'HR', 'Sales', 'Customer Service', 'Other'];

export default function SOPLibraryPage() {
  const navigate = useNavigate();
  const [sops, setSOPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => { fetchSOPs(); }, [category]);
  useEffect(() => { if (searchTimeout) clearTimeout(searchTimeout); const t = setTimeout(() => fetchSOPs(), 500); setSearchTimeout(t); return () => clearTimeout(t); }, [search]);

  const fetchSOPs = async () => {
    try { const params = {}; if (category !== 'All') params.category = category; if (search.trim()) params.search = search.trim(); const { data } = await api.get(`/api/sops`, { params }); setSOPs(data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="sop-library-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#AAFF00] tracking-tight">SOP Library</h1>
          <p className="text-[#9CA3AF] mt-1">Search and browse your company's SOPs.</p>
        </div>
        <Button onClick={() => navigate('/capture')} className="bg-[#AAFF00] text-[#080812] hover:bg-[#c4ff4d] gap-2 shrink-0 font-semibold" data-testid="library-new-capture-btn"><Plus size={16} /> New Capture</Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search SOPs..." className="pl-9 bg-[#1F2937] border-[#374151] text-[#F9FAFB] placeholder:text-[#9CA3AF]" data-testid="sop-search-input" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-48 bg-[#1F2937] border-[#374151] text-[#F9FAFB]" data-testid="sop-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111827] border-[#374151]">{categories.map(c => <SelectItem key={c} value={c} className="text-[#F9FAFB] focus:bg-[#1F2937]">{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="bg-[#111827] rounded-lg border border-[#374151] p-5 animate-pulse"><div className="h-4 bg-[#1F2937] rounded w-3/4 mb-3" /><div className="h-3 bg-[#1F2937] rounded w-1/2" /></div>)}
        </div>
      ) : sops.length === 0 ? (
        <div className="text-center py-16 bg-[#111827] rounded-lg border border-[#374151]">
          <FileText size={40} className="mx-auto text-[#374151] mb-4" />
          <p className="text-[#9CA3AF] text-lg">No SOPs found</p>
          <p className="text-sm text-[#9CA3AF]/70 mt-1">{search ? 'Try a different search.' : 'Create your first capture.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sops.map(sop => (
            <button key={sop.id} onClick={() => navigate(`/library/${sop.id}`)} className="bg-[#111827] rounded-lg border border-[#374151] p-5 text-left hover:border-[#AAFF00]/40 transition-all duration-200" data-testid={`sop-card-${sop.id}`}>
              <h3 className="font-medium text-[#F9FAFB] truncate mb-2">{sop.title}</h3>
              <p className="text-xs text-[#9CA3AF] mb-3">{sop.category} &middot; {new Date(sop.created_at).toLocaleDateString()}</p>
              <p className="text-sm text-[#9CA3AF]/80 line-clamp-2">{sop.content?.substring(0, 120)}...</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
